import { useEffect, useState } from 'react';
import { Alert, AlertColor, Box, Button, Card, CardContent, CircularProgress, FormControl, Grid, Icon, InputLabel, ListItemText, MenuItem, OutlinedInput, Paper, Select, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { useVForm } from 'shared/forms';

import { FerramentasDeDetalhe, InputError, ConfirmacaoModal } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { t } from 'i18next';
import { GrupoTesteService } from 'shared/services/grupoTeste/grupoTesteService';
import { ICreateGrupoTeste, InicitalCreateGrupoTeste, listKeyValueTipoGrupoTeste } from 'shared/types';
import { useHasPermissionAcao } from 'shared/hooks';
import { useSelectedOrganization } from 'shared/store/organization';
import { CanaisService } from 'shared/services/canais/canaisService';
import { IKeyValue, listagemCanalToIKeyValue } from 'shared/utils';


const validationSchema = yup.object({
  IdComCanal: yup.string().required(),
  NomeGrupoTeste: yup.string().required(),
  TipoGrupoTeste: yup.string().required(),
});

export const DetalheDeGrupoTeste: React.FC = () => {
  const {idCanal} = useParams<'idCanal'>();
  const { id = 'novo' } = useParams<'id'>();
  const org = useSelectedOrganization();
  const navigate = useNavigate();

  const hasPermitionCreate = useHasPermissionAcao('grupoteste','CRE');
  const hasPermitionDelete = useHasPermissionAcao('grupoteste','DEL');
  const hasPermitionUpdate = useHasPermissionAcao('grupoteste','UPD');

  
  const [alert, setAlert] = useState<null | { text: string; type: AlertColor }>(null);
  const { formRef, save, isSaveAndClose } = useVForm();

  const { register, handleSubmit, reset, setValue, watch, formState: { errors }, } = useForm({
    resolver: yupResolver(validationSchema),
    reValidateMode: 'onBlur'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCanal, setIsLoadingCanal] = useState(false);
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [titleConfirmacao, setTitleConfirmacao] = useState('');
  const [messageConfirmacao, setMessageConfirmacao] = useState('');


  const selectCanal = watch('IdComCanal');
  const selectTipo = watch('TipoGrupoTeste');

  
  const [listKeyValueCanal, setListKeyValueCanal] = useState<IKeyValue[]>([]);

  useEffect(() => {
    setIsLoadingCanal(true);

    CanaisService.getAll()
      .then((result) => {
        setIsLoadingCanal(false);
        if (result instanceof Error) {
          console.log(`Ocorreu o seguinte erro: ${result.message}`);
        } else {
          setListKeyValueCanal(listagemCanalToIKeyValue(result));
        }
      });
  },[]);


  useEffect(() => {
    setAlert(null);
    if (id !== 'novo') {
      setIsLoading(true);

      GrupoTesteService.getById(Number(org?.CodigoOrganizacao), Number(idCanal), Number(id))
        .then((result) => {
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
            navigate('/grupoteste/listagem');
          } else {
            delete result.ROW_NUM;
            reset(result);
          }
          setIsLoading(false);
        });
    }
    else {
      reset(InicitalCreateGrupoTeste);
    }

  }, [id]);

  const handleDelete = () => {
    setTitleConfirmacao(t('components.actions.apagar'));
    setMessageConfirmacao(t('messages.grupoTeste.exclusion') + id + '?');
    setOpenConfirmacao(true);
    //console.log('handleDelete');
  };

  const confirmDelete = () => {
    setAlert(null);
    GrupoTesteService.deleteById(Number(org?.CodigoOrganizacao), Number(selectCanal),Number(id))
      .then(result => {
        if (result instanceof Error) {
          setAlert({
            text: result.message,
            type: 'error',
          });
          setOpenConfirmacao(false);
        } else {
          setAlert({
            text: 'Registro apagado com sucesso!',
            type: 'success',
          });
          navigate('/grupoteste/listagem');
        }
      })
      .catch( reason => {
        setAlert({
          text: reason,
          type: 'error',
        });
        setOpenConfirmacao(false);
      });
  };

  const onSubmit = async (data: ICreateGrupoTeste) => {
    setAlert(null);
    setIsSubmitting(true);
    if (id === 'novo') {
      GrupoTesteService
        .create(Number(org?.CodigoOrganizacao), Number(selectCanal),data)
        .then((result) => { 
          setIsLoading(false);

          if (result instanceof Error) {  
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate('/grupoteste/listagem');
            } else {
              navigate(`/grupoteste/detalhe/${selectCanal}/${result}`);
            }
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    } else {
      GrupoTesteService
        .updateById(Number(org?.CodigoOrganizacao), Number(selectCanal), Number(id), data)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate('/grupoteste/listagem');
            }
            setAlert({
              text: t('messages.success.alterado'), 
              type: 'success',
            });

          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    }

  };

  return (
    <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Novo Grupo Teste / Seed List' : hasPermitionUpdate?'Alterar Grupo Teste / Seed List':'Consultar Grupo Teste / Seed List'}
      
      barraDeFerramentas={!(isLoading || isSubmitting) && (
        <FerramentasDeDetalhe
          idForm='formDetalheDegrupoTeste'
          textoBotaoNovo={t('components.actions.novo')}
          textoBotaoGenerico='Destinatario'
          iconeBotaoGenerico='recent_actors'

          mostrarBotaoNovo={id !== 'novo' && hasPermitionCreate}
          mostrarBotaoApagar={id !== 'novo' && hasPermitionDelete}
          mostrarBotaoSalvar={hasPermitionCreate || hasPermitionUpdate}
          mostrarBotaoGenerico={id !== 'novo'}

          aoClicarEmSalvar={save}
          aoClicarEmVoltar={() => navigate('/grupoteste/listagem')}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/grupoteste/detalhe/0/novo')}
          aoClicarEmGenerico={() => navigate(`/grupoteste/detalhe/${idCanal}/${id}/destinatario`)}
        />)
      }
    >
      <Box ref={formRef} id='formDetalheDegrupoTeste' component="form" display="flex" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" width='100%' >

          <Card >
            <CardContent sx={{ padding: 0 }} >
              <div>
                {alert ? (
                  <>
                    <Grid item xs={12} paddingX={2}>
                      <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ marginTop: '1rem', marginBottom: '0' }}>
                        {alert.text}
                      </Alert>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} component={Typography} paddingX={2}>

                  </Grid>
                )}
                <Grid container  >
                  <Grid item xs={12} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }}>
                      <InputLabel id='label-select-canal'>{`${t('pages.grupoTeste.canal')} *`}</InputLabel>
                      <Select
                        disabled={isSubmitting || isLoading || id !== 'novo'}
                        error={errors?.IdComCanal?.type ? true : false}
                        labelId='label-select-canal'
                        id='IdComCanal'
                        input={<OutlinedInput label={`${t('pages.grupoTeste.canal')} *`} />}
                        {...register('IdComCanal')}
                        value={selectCanal || ''}
                      >
                        <MenuItem value=''>
                          <ListItemText primary='' />
                        </MenuItem>
                        {listKeyValueCanal.map((item) => (
                          <MenuItem key={item.key} value={item.key} >
                            <ListItemText primary={item.value} sx={{ margin: '0' }} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {errors?.IdComCanal?.type && <InputError type={errors.IdComCanal.type} field='IdComCanal' />}

                  </Grid>
                  <Grid item xs={12} paddingX={2}>
                    <TextField
                      error={errors?.NomeGrupoTeste?.type ? true : false}
                      fullWidth
                      id='NomeGrupoTeste'
                      type='text'
                      label={`${t('pages.grupoTeste.nome')} *`}
                      disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                      {...register('NomeGrupoTeste')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.NomeGrupoTeste?.type && <InputError type={errors.NomeGrupoTeste.type} field='NomeGrupoTeste' />}
                  </Grid>
                  <Grid item xs={12} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }}>
                      <InputLabel id='label-select-tipo'>{`${t('pages.grupoTeste.tipo')} *`}</InputLabel>
                      <Select
                        disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                        error={errors?.TipoGrupoTeste?.type ? true : false}
                        labelId='label-select-tipo'
                        id='TipoGrupoTeste'
                        input={<OutlinedInput label={`${t('pages.grupoTeste.tipo')} *`} />}
                        {...register('TipoGrupoTeste')}
                        value={selectTipo !== undefined ? selectTipo.trim() : ''}
                      >
                        <MenuItem value=''>
                          <ListItemText primary='' />
                        </MenuItem>
                        {listKeyValueTipoGrupoTeste.map((item) => (
                          <MenuItem key={item.key} value={item.key} >
                            <ListItemText primary={item.value} sx={{ margin: '0' }} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {errors?.TipoGrupoTeste?.type && <InputError type={errors.TipoGrupoTeste.type} field='TipoGrupoTeste' />}
                  </Grid>
                </Grid>
              </div>
            </CardContent>
          </Card>
        </Box>
        <ConfirmacaoModal
          open={openConfirmacao}
          title={titleConfirmacao}
          message={messageConfirmacao}
          onClose={() => setOpenConfirmacao(false)}
          onConfirm={() => confirmDelete()}
        />
      </Box>
      {(isLoading || isSubmitting) && (<Box sx={{
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <CircularProgress />
      </Box>)}

    </LayoutBaseDePagina >
  );
};
